import axios from "axios";
import type { ApiError } from "~/types/auth";

function parseRetryAfterSeconds(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value) && value >= 0) {
    return Math.ceil(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
      return undefined;
    }

    const asNumber = Number(trimmed);
    if (Number.isFinite(asNumber) && asNumber >= 0) {
      return Math.ceil(asNumber);
    }

    const asDate = Date.parse(trimmed);
    if (!Number.isNaN(asDate)) {
      const seconds = Math.ceil((asDate - Date.now()) / 1000);
      return Math.max(0, seconds);
    }
  }

  if (Array.isArray(value)) {
    for (const candidate of value) {
      const parsed = parseRetryAfterSeconds(candidate);
      if (parsed !== undefined) {
        return parsed;
      }
    }
  }

  return undefined;
}

function parseRetryAfterFromMessage(message: string | undefined): number | undefined {
  if (!message) {
    return undefined;
  }

  const matched = message.match(/(?:try again in|retry after)\s+(\d+)\s*seconds?/i);
  if (!matched) {
    return undefined;
  }

  return parseRetryAfterSeconds(matched[1]);
}

function extractRetryAfter(data: ApiError | undefined, retryAfterHeader: unknown) {
  const details = data?.error?.details;
  const detailsObject =
    details && typeof details === "object"
      ? (details as { retryAfter?: unknown; retry_after?: unknown })
      : undefined;
  const retryAfterInMessage = parseRetryAfterFromMessage(data?.error?.message);

  const candidates: unknown[] = [
    data?.retryAfter,
    (data as { retry_after?: unknown } | undefined)?.retry_after,
    detailsObject?.retryAfter,
    detailsObject?.retry_after,
    retryAfterHeader,
    retryAfterInMessage,
  ];

  for (const candidate of candidates) {
    const parsed = parseRetryAfterSeconds(candidate);
    if (parsed !== undefined) {
      return parsed;
    }
  }

  return undefined;
}

export function getApiErrorMessage(error: unknown, fallback: string) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiError | undefined;
    const message = data?.error?.message;
    const retryAfterHeader = error.response?.headers?.["retry-after"];

    return {
      message: message && message.length > 0 ? message : fallback,
      retryAfter: extractRetryAfter(data, retryAfterHeader),
    };
  }

  return { message: fallback };
}

export function getApiFieldErrors(error: unknown) {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as ApiError | undefined;
    const fields = data?.error?.details?.fields;
    if (fields && typeof fields === "object") {
      return fields as Record<string, string>;
    }
  }
  return null;
}
